import {FC, useState, useMemo} from 'react';
import {
  GET_STATE_REPORT,
  FILTER_USERS,
  GET_CATALOGS_BY_TYPE,
} from 'graphql/absences/Querys';
import {useTranslation} from 'react-i18next';
import {Box, Typography, Alert} from '@mui/material';
import Button from 'components/button/Button';
import {useGraphqlQuery} from 'hooks/useGraphqlQuery';
import SearchBarController from 'controllers/SearchBarController/SearchBarController';
import DropDownController from 'controllers/DropDownController/DropDownController';
import DateRangePicker from 'components/DateRangePicker/DateRangePicker';
import Chip from 'components/Chip/Chip';
import LoaderWithBackdrop from 'components/LoaderWithBackdrop';
import {DropDownItem} from 'components/DropDownInput/DropDownInput';
import './Reports.scss';

interface FilterReportVariables {
  toDate: string;
  country?: string;
  ids?: string[];
}

interface UserAutocompleteItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
}

const hasCountrySelected = (country: DropDownItem | null): boolean =>
  !!country && country.key !== '';

const Reports: FC = () => {
  const {t} = useTranslation();
  const [selectedUser, setSelectedUser] = useState<UserAutocompleteItem | null>(
    null
  );
  const [selectedCountry, setSelectedCountry] = useState<DropDownItem | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const {fetchData, isLoading} = useGraphqlQuery(
    '',
    GET_STATE_REPORT,
    'currentStateReport'
  );

  // Check if form is valid (only date is required)
  const isFormValid = useMemo(() => {
    return selectedDate !== null;
  }, [selectedDate]);

  // Build filter variables for GraphQL query
  const buildFilterVariables = (): FilterReportVariables => {
    const filter: FilterReportVariables = {
      toDate: selectedDate!.toISOString().split('T')[0],
    };

    if (selectedUser) {
      filter.ids = [selectedUser.id];
    } else if (hasCountrySelected(selectedCountry)) {
      filter.country = selectedCountry!.description;
    }

    return filter;
  };

  // Handle CSV download from URL or base64
  const handleDownload = async (reportData: string) => {
    if (!reportData) {
      return;
    }

    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const fileName = `palowan_reporte_${timestamp}.csv`;

    if (reportData.startsWith('data:')) {
      try {
        const base64Data = reportData.split(',')[1];
        const csvContent = atob(base64Data);
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8'});
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        setErrorMessage('Error al decodificar el archivo CSV');
      }
    } else {
      // URL response — try fetching as blob to control filename,
      // fallback to window.open if CORS blocks the request
      try {
        const response = await fetch(reportData);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(reportData, '_blank');
      }
    }
  };

  // Handle form submission
  const handleGenerateReport = async () => {
    if (!isFormValid) {
      setErrorMessage('La fecha final es obligatoria para generar el reporte');
      return;
    }

    setErrorMessage('');
    try {
      const filter = buildFilterVariables();
      const result = await fetchData({filter});
      if (result) {
        handleDownload(result as string);
      }
    } catch (error) {
      setErrorMessage('Error al generar el reporte');
    }
  };

  // Clear user selection
  const clearUserSelection = () => {
    setSelectedUser(null);
  };

  const isCountryActive = hasCountrySelected(selectedCountry);

  return (
    <Box sx={{pt: 4, px: 3}}>
      <LoaderWithBackdrop isLoading={!!isLoading} />
      {errorMessage && (
        <Alert severity="error" sx={{mb: 3}}>
          {errorMessage}
        </Alert>
      )}

      {/* Name Filter */}
      <Box sx={{mb: 3}}>
        <Typography variant="body2" sx={{mb: 1, color: 'text.secondary'}}>
          {t('leaves.Reports.name')}
        </Typography>
        <Box
          className="search-bar-container"
          sx={{
            opacity: isCountryActive ? 0.5 : 1,
            pointerEvents: isCountryActive ? 'none' : 'auto',
            '& .MuiPaper-root': {width: '100%'},
          }}
        >
          <SearchBarController<UserAutocompleteItem>
            query={FILTER_USERS}
            queryVariableName="keyword"
            placeholder={t('leaves.Reports.searchName')}
            transformResult={user => ({
              id: user.id,
              text: `${user.firstName} ${user.lastName}`,
              selected: false,
            })}
            onResultSelect={user => {
              setSelectedUser(user);
              setSelectedCountry(null);
            }}
          />
        </Box>
        {selectedUser && (
          <Box sx={{mt: 1}}>
            <Chip
              id="selected-user"
              text={`${selectedUser.firstName} ${selectedUser.lastName}`}
              size="small"
              color="primary"
              onRemove={clearUserSelection}
            />
          </Box>
        )}
      </Box>

      {/* Country Filter */}
      <Box sx={{mb: 3}}>
        <Typography variant="body2" sx={{mb: 1, color: 'text.secondary'}}>
          {t('leaves.Reports.country')}
        </Typography>
        <Box
          sx={{
            opacity: selectedUser ? 0.5 : 1,
            pointerEvents: selectedUser ? 'none' : 'auto',
          }}
        >
          <DropDownController
            query={GET_CATALOGS_BY_TYPE}
            queryVariables={{
              type: 'COUNTRY_CODE',
            }}
            rootField="catalogsByType"
            transformResult={item => ({
              key: item.id,
              description: item.name,
            })}
            defaultOption={{
              key: '',
              description: t('leaves.Reports.selectCountry'),
            }}
            value={selectedCountry ? String(selectedCountry.key) || '' : ''}
            onChange={value => {
              if (value && value.key !== '') {
                setSelectedCountry(value);
                setSelectedUser(null);
              } else {
                setSelectedCountry(null);
              }
            }}
          />
        </Box>
      </Box>

      {/* Date Filter - Required */}
      <Box sx={{mb: 3}}>
        <DateRangePicker
          label={`${t('leaves.Reports.toDate')} *`}
          selectionType="single"
          onChangeDates={(from: Date | null) => {
            setSelectedDate(from);
          }}
        />
      </Box>

      {/* Generate Report Button */}
      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button
          text={isLoading ? 'Generando...' : t('leaves.Reports.submit')}
          onClick={handleGenerateReport}
          disabled={!isFormValid || !!isLoading}
        />
      </Box>
    </Box>
  );
};

export default Reports;
